import { useProvisionApp } from '@my/directory-api/hooks';
import type { AppNameValue, SupportAccount } from '@my/directory-api';
import { Button, SelectInput, showToast } from '@my/ui';
import { useState } from 'react';

/**
 * Apps a customer can be given. `Hub` is absent on purpose: every account has
 * the account portal by definition, so there is no subscription to sell and
 * offering one would write a meaningless row.
 */
const PROVISIONABLE_APPS: AppNameValue[] = ['Tms', 'Depot', 'Subsistence', 'Safety', 'Check'];

const PLANS = ['Trial', 'Basic', 'Professional', 'Enterprise'] as const;

/**
 * Turn an app on for this customer.
 *
 * <p>
 * Written as the customer's own screen, like the rest of My Hub — nothing here
 * asks whether the viewer is staff. The server decides who receives the app
 * role, and for a support caller that is the account's owners rather than the
 * caller.
 * </p>
 */
export function EnableAppPanel({ account }: { account: SupportAccount }) {
  const provision = useProvisionApp();
  const [app, setApp] = useState<AppNameValue | ''>('');
  const [organisationId, setOrganisationId] = useState(account.organisations[0]?.id ?? '');
  const [plan, setPlan] = useState<(typeof PLANS)[number]>('Basic');

  // Offered when there is no LIVE subscription: re-provisioning is exactly how
  // a lapsed app is switched back on, so an expired row must not hide it.
  const live = account.subscriptions.filter((s) => s.isActive).map((s) => s.appKey);
  const available = PROVISIONABLE_APPS.filter((a) => !live.includes(a));

  // Provisioning grants a role IN an organisation, so without one there is
  // nothing to grant. Say so rather than failing at the server.
  if (account.organisations.length === 0) {
    return (
      <p className="text-xs italic text-slate-400">
        This account has no organisation yet — one is needed before an app can be enabled.
      </p>
    );
  }

  if (available.length === 0) {
    return (
      <p className="text-xs italic text-slate-400">
        Every app is already enabled for this account.
      </p>
    );
  }

  const submit = () => {
    if (!app || !organisationId) return;
    provision.mutate(
      { appKey: app, organisationId, plan },
      {
        onSuccess: (res) => {
          showToast({
            type: 'success',
            message: res.subscriptionCreated
              ? `${app} enabled for ${account.name}.`
              : `${app} was already enabled; access re-checked.`,
          });
          setApp('');
        },
        onError: (err) => showToast({ type: 'error', message: err.message }),
      },
    );
  };

  return (
    <div className="space-y-3 text-sm">
      <SelectInput
        label="App"
        placeholder="Choose an app…"
        options={available}
        value={app}
        onChange={(e) => setApp(e.target.value as AppNameValue)}
      />

      <SelectInput
        label="Organisation"
        options={account.organisations.map((o) => ({ label: o.name, value: o.id }))}
        value={organisationId}
        onChange={(e) => setOrganisationId(e.target.value)}
      />

      <SelectInput
        label="Plan"
        options={[...PLANS]}
        value={plan}
        onChange={(e) => setPlan(e.target.value as (typeof PLANS)[number])}
      />

      <Button onClick={submit} disabled={!app || provision.isPending}>
        {provision.isPending ? 'Enabling…' : 'Enable app'}
      </Button>
    </div>
  );
}
